import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

const STATUS_BADGE = {
  open: 'badge-blue',
  received: 'badge-green',
  partial: 'badge-orange',
  cancelled: 'badge-red',
};

const inp = {
  fontSize: 13,
  background: '#f8fafc',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  color: '#1e293b',
  padding: '5px 8px',
  outline: 'none',
};

/* Searchable inventory dropdown with free-text fallback */
function InventoryCombobox({ value, inventoryItems, onChange, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // Display label when an item is selected
  const selectedItem = inventoryItems.find(i => i.id === value);

  function handleSelect(item) {
    onChange(item);
    setQuery('');
    setOpen(false);
  }

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.length > 0
    ? inventoryItems.filter(i => i.name.toLowerCase().includes(query.toLowerCase()))
    : inventoryItems;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div
        style={{
          ...inp,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          gap: 4,
          padding: '4px 8px',
          minWidth: 140,
        }}
        onClick={() => setOpen(o => !o)}
      >
        {selectedItem
          ? <span style={{ flex: 1, fontSize: 13, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedItem.name}</span>
          : <span style={{ flex: 1, fontSize: 13, color: '#94a3b8' }}>{placeholder || '— link inventory —'}</span>
        }
        <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>▾</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 50,
          background: 'white',
          border: '1px solid #cbd5e1',
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          width: 240,
          maxHeight: 240,
          overflowY: 'auto',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search inventory..."
              style={{ ...inp, width: '100%', padding: '4px 8px' }}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div
            style={{ padding: '6px 10px', fontSize: 12, color: '#64748b', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
            onClick={() => { onChange(null); setQuery(''); setOpen(false); }}
          >
            — no link —
          </div>
          {filtered.map(item => (
            <div
              key={item.id}
              style={{
                padding: '7px 10px',
                fontSize: 13,
                cursor: 'pointer',
                color: '#0f172a',
                background: value === item.id ? '#f0fdfa' : 'transparent',
                borderBottom: '1px solid #f8fafc',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = value === item.id ? '#f0fdfa' : 'transparent'}
              onClick={() => handleSelect(item)}
            >
              <div style={{ fontWeight: value === item.id ? 600 : 400 }}>{item.name}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                In stock: {item.quantity_on_hand} {item.unit}
                {item.par_level > 0 && item.quantity_on_hand < item.par_level && (
                  <span style={{ color: '#ef4444', marginLeft: 6 }}>↓ below par</span>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '10px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>No matches</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PurchaseOrders({ onOrderCreated }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');
  const [vendors, setVendors] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // New PO form state
  const [form, setForm] = useState({ vendor_name: '', vendor_id: '', expected_date: '', notes: '' });
  const [items, setItems] = useState([{ item_name: '', unit: '', quantity_ordered: '', unit_price: '', inventory_item_id: '', freeText: false }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/purchase-orders'),
      api.get('/vendors'),
    ]).then(([po, v]) => {
      setOrders(po.data);
      setVendors(v.data);
    }).finally(() => setLoading(false));
  }, []);

  // When the "New Order" tab opens, fetch full inventory with par levels
  useEffect(() => {
    if (tab === 'new' && inventory.length === 0) {
      setInventoryLoading(true);
      api.get('/inventory?limit=500')
        .then(({ data }) => {
          const list = Array.isArray(data) ? data : (data.items || []);
          setInventory(list.sort((a, b) => a.name.localeCompare(b.name)));
        })
        .catch(() => {})
        .finally(() => setInventoryLoading(false));
    }
  }, [tab]);

  function addItem() {
    setItems(prev => [...prev, { item_name: '', unit: '', quantity_ordered: '', unit_price: '', inventory_item_id: '', freeText: false }]);
  }

  function removeItem(i) {
    setItems(prev => prev.filter((_, idx) => idx !== i));
  }

  function updateItem(i, field, value) {
    setItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: value } : item));
  }

  function selectInventoryItem(i, invItem) {
    if (invItem) {
      setItems(prev => prev.map((item, idx) => idx === i ? {
        ...item,
        inventory_item_id: invItem.id,
        item_name: invItem.name,
        unit: invItem.unit,
      } : item));
    } else {
      setItems(prev => prev.map((item, idx) => idx === i ? { ...item, inventory_item_id: '' } : item));
    }
  }

  function addBelowParItems() {
    const belowPar = inventory.filter(inv =>
      inv.par_level > 0 && parseFloat(inv.quantity_on_hand) < parseFloat(inv.par_level)
    );
    if (belowPar.length === 0) {
      setMsg('All items are at or above par level.');
      setTimeout(() => setMsg(''), 3000);
      return;
    }
    const newRows = belowPar.map(inv => ({
      item_name: inv.name,
      unit: inv.unit,
      quantity_ordered: String(Math.ceil(parseFloat(inv.par_level) - parseFloat(inv.quantity_on_hand))),
      unit_price: '',
      inventory_item_id: inv.id,
      freeText: false,
    }));
    // Append, replacing any blank starter row
    setItems(prev => {
      const nonEmpty = prev.filter(i => i.item_name || i.quantity_ordered);
      return [...nonEmpty, ...newRows];
    });
  }

  async function createPO(e) {
    e.preventDefault();
    const validItems = items.filter(i => i.item_name && i.quantity_ordered);
    if (!validItems.length) { setMsg('Add at least one item.'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/purchase-orders', {
        ...form,
        vendor_id: form.vendor_id || null,
        items: validItems.map(i => ({
          item_name: i.item_name,
          unit: i.unit,
          quantity_ordered: parseFloat(i.quantity_ordered),
          unit_price: i.unit_price ? parseFloat(i.unit_price) : null,
          inventory_item_id: i.inventory_item_id || null,
        })),
      });
      setOrders(prev => [data, ...prev]);
      setTab('list');
      setForm({ vendor_name: '', vendor_id: '', expected_date: '', notes: '' });
      setItems([{ item_name: '', unit: '', quantity_ordered: '', unit_price: '', inventory_item_id: '', freeText: false }]);
      setMsg('Purchase order created!');
      setTimeout(() => setMsg(''), 3000);
      onOrderCreated?.();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to create PO');
    } finally { setSaving(false); }
  }

  async function cancelPO(id) {
    try {
      await api.put(`/purchase-orders/${id}`, { status: 'cancelled' });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: 'cancelled' } : o));
    } catch { alert('Failed'); }
  }

  if (loading) return <div className="loading">Loading orders...</div>;

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button className={`btn btn-sm ${tab === 'list' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('list')}>Open Orders ({orders.filter(o => o.status === 'open').length})</button>
        <button className={`btn btn-sm ${tab === 'new' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('new')}>+ New Order</button>
      </div>

      {msg && <div className={`alert ${msg.includes('Failed') || msg.includes('Add') ? 'alert-error' : 'alert-success'} mb-4`}>{msg}</div>}

      {tab === 'list' && (
        <div>
          {orders.length === 0 && (
            <div className="card"><div className="card-body text-gray" style={{ textAlign: 'center', padding: 40 }}>
              No purchase orders yet. Create one before placing an order with a vendor.
            </div></div>
          )}
          {orders.map(o => (
            <div className="card mb-3" key={o.id}>
              <div className="card-body" style={{ padding: '12px 16px' }}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-semibold" style={{ fontSize: 15, color: '#0f172a' }}>{o.vendor_name}</span>
                    <span className={`badge ${STATUS_BADGE[o.status] || 'badge-gray'} ml-2`}>{o.status}</span>
                    <span className="text-gray text-sm" style={{ marginLeft: 10 }}>{o.item_count} items</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray text-sm">{o.expected_date ? `Expected ${new Date(o.expected_date + 'T12:00:00').toLocaleDateString()}` : new Date(o.created_at).toLocaleDateString()}</span>
                    {o.status === 'open' && (
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: '#ef4444' }} onClick={() => cancelPO(o.id)}>Cancel</button>
                    )}
                  </div>
                </div>
                {o.notes && <p className="text-gray text-sm" style={{ marginTop: 4 }}>{o.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'new' && (
        <div className="card">
          <div className="card-header">New Purchase Order</div>
          <div className="card-body">
            <form onSubmit={createPO}>
              <div className="form-row">
                <div className="form-group">
                  <label>Vendor *</label>
                  <select value={form.vendor_id} onChange={e => {
                    const v = vendors.find(x => x.id === e.target.value);
                    setForm(f => ({ ...f, vendor_id: e.target.value, vendor_name: v ? v.name : f.vendor_name }));
                  }}>
                    <option value="">— Select vendor or type below —</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Vendor Name (if not in list)</label>
                  <input value={form.vendor_name} onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value, vendor_id: '' }))} placeholder="e.g. Kona Farms" />
                </div>
                <div className="form-group">
                  <label>Expected Delivery</label>
                  <input type="date" value={form.expected_date} onChange={e => setForm(f => ({ ...f, expected_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
                </div>
              </div>

              <div style={{ marginTop: 16, marginBottom: 8 }}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-sm" style={{ color: '#0f172a' }}>Order Items</span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ fontSize: 12, color: '#0d9488', border: '1px solid #0d9488' }}
                      onClick={addBelowParItems}
                      disabled={inventoryLoading}
                    >
                      {inventoryLoading ? 'Loading...' : '+ Add Below-Par Items'}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={addItem}>+ Add Item</button>
                  </div>
                </div>

                <div style={{ borderRadius: 8, overflow: 'visible', border: '1px solid #e2e8f0' }}>
                  {/* Header row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 80px 80px 80px 24px',
                    gap: 8,
                    padding: '8px 12px',
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}>
                    <div>Item</div>
                    <div>Qty</div>
                    <div>Unit</div>
                    <div>Unit Price</div>
                    <div></div>
                  </div>

                  {/* Item rows */}
                  {items.map((item, i) => {
                    const linkedInv = inventory.find(inv => inv.id === item.inventory_item_id);
                    return (
                      <div key={i} style={{ borderBottom: i < items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                        {/* Main row */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 80px 80px 80px 24px',
                          gap: 8,
                          padding: '8px 12px',
                          alignItems: 'start',
                        }}>
                          {/* Item selector / free text toggle */}
                          <div>
                            {item.freeText ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input
                                  value={item.item_name}
                                  onChange={e => updateItem(i, 'item_name', e.target.value)}
                                  placeholder="Item name"
                                  style={{ ...inp, flex: 1 }}
                                  required
                                />
                                <button
                                  type="button"
                                  title="Switch to inventory picker"
                                  onClick={() => updateItem(i, 'freeText', false)}
                                  style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#64748b', padding: '4px 6px', flexShrink: 0 }}
                                >
                                  ≡
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                  <InventoryCombobox
                                    value={item.inventory_item_id}
                                    inventoryItems={inventory}
                                    onChange={invItem => selectInventoryItem(i, invItem)}
                                  />
                                </div>
                                <button
                                  type="button"
                                  title="Type a custom item name"
                                  onClick={() => updateItem(i, 'freeText', true)}
                                  style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#64748b', padding: '4px 6px', flexShrink: 0 }}
                                >
                                  T
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Qty */}
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity_ordered}
                            onChange={e => updateItem(i, 'quantity_ordered', e.target.value)}
                            style={{ ...inp, width: '100%' }}
                            required
                          />

                          {/* Unit */}
                          <input
                            value={item.unit}
                            onChange={e => updateItem(i, 'unit', e.target.value)}
                            placeholder="lb"
                            style={{ ...inp, width: '100%' }}
                          />

                          {/* Unit Price */}
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={e => updateItem(i, 'unit_price', e.target.value)}
                            placeholder="0.00"
                            style={{ ...inp, width: '100%' }}
                          />

                          {/* Delete */}
                          <div style={{ paddingTop: 4 }}>
                            {items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeItem(i)}
                                style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>

                        {/* In-stock indicator beneath row */}
                        {linkedInv && (
                          <div style={{ paddingLeft: 12, paddingBottom: 6, fontSize: 11, color: '#64748b' }}>
                            In stock: <strong style={{ color: parseFloat(linkedInv.quantity_on_hand) < parseFloat(linkedInv.par_level) ? '#ef4444' : '#0f172a' }}>
                              {linkedInv.quantity_on_hand} {linkedInv.unit}
                            </strong>
                            {linkedInv.par_level > 0 && (
                              <span style={{ marginLeft: 8 }}>· Par: {linkedInv.par_level} {linkedInv.unit}</span>
                            )}
                          </div>
                        )}
                        {!linkedInv && !item.freeText && item.item_name && (
                          <div style={{ paddingLeft: 12, paddingBottom: 6, fontSize: 11, color: '#94a3b8' }}>
                            Not linked to inventory
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Purchase Order'}</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setTab('list')}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
